import { GlueErrorDetail, GlueErrorResponse } from '@/src/lib/glue/types';
import { isGlueErrorDetail, isGlueErrorResponse } from '@/src/lib/glue/business/is-glue-error';
import {
  AUTH0_INACTIVE_SESSION_MESSAGE,
  AUTH0_INVALID_SESSION_MESSAGE,
  AUTH0_MISSING_ACCESS_TOKEN_MESSAGE,
  CART_CONTAINS_NON_BUYABLE_ITEMS,
  CART_NEEDS_REVIEW,
  CART_PRICES_NOT_VALIDATED_TRY_AGAIN_ERROR_CODE,
  CART_PRICES_UPDATED_TRY_AGAIN_ERROR_CODE,
  CUSTOMER_NOT_FOUND,
  ERROR_CODE_REDIRECT_MAP,
  RESPONSE_CODE_STRIPE_PAYMENT_AUTHENTICATION_ERROR,
  RESPONSE_CODE_STRIPE_PAYMENT_ERROR,
  UNAUTHORIZED,
} from '@/src/lib/utils/constants';
import { GlueApiError } from '@/src/lib/http/business/http-error';
import { HTTP_STATUS } from '@/src/lib/http/types';
import { isValidElement, ReactNode } from 'react';
import { transformURLForFrontendPaths } from '@/src/lib/utils/url.ts';

export function getRedirectPathForErrorCode(
  error: Error | GlueApiError | unknown,
  lang: string,
  country: string,
  fallbackPath: string,
) {
  if (isGlueErrorResponse(error) && ERROR_CODE_REDIRECT_MAP.get(error.errors[0].code)) {
    return transformURLForFrontendPaths(`${ERROR_CODE_REDIRECT_MAP.get(error.errors[0].code)}`, lang, country);
  }

  return fallbackPath || transformURLForFrontendPaths('/', lang, country);
}

function cleanErrorString(error: string): string {
  let cleanedError = error;
  while (cleanedError.indexOf('Error: ') > -1) cleanedError = cleanedError.replace('Error: ', '');
  return cleanedError;
}

function isJsonString(str: unknown): str is string {
  try {
    // @ts-expect-error We want to determine the type of str
    JSON.parse(str);
  } catch (_) {
    return false;
  }
  return true;
}

export function transformExceptionToErrorObject(
  error: Error | GlueErrorResponse | string | unknown,
): GlueErrorResponse | Error {
  let err: typeof error = error;

  if (isJsonString(error)) {
    err = JSON.parse(error);
  }

  if (!(err instanceof Error) && isGlueErrorResponse(err)) {
    return err;
  }

  if (isAuthenticationErrorMessage(getErrorMessageFromAnyErrorLike(err))) {
    if (typeof error === 'string') {
      return { errors: [{ detail: error, code: '0', status: 403 }] };
    }
    return { errors: [error as GlueErrorDetail] };
  }

  const cleanedError = cleanErrorString(String(err));
  if (cleanedError.indexOf('{') === -1) {
    const status = (err as GlueErrorResponse).errors?.[0]?.status || 500;
    const code = (err as GlueErrorResponse).errors?.[0]?.code || '0';

    return {
      errors: [
        {
          detail: cleanedError,
          code,
          status,
        },
      ],
    };
  }

  if (isJsonString(cleanedError)) {
    const parsedError = JSON.parse(cleanedError);
    if (isGlueErrorResponse(parsedError)) {
      const glueErrors = parsedError.errors.map((glueError) => ({
        ...glueError,
        code: (glueError.code || 400).toString(),
      }));

      return { errors: glueErrors };
    }
    return { errors: [parsedError] };
  }

  return { errors: [JSON.parse(JSON.stringify(error))] } satisfies GlueErrorResponse;
}

export function transformApiErrorsForFormik(errorMessages: Array<GlueErrorDetail | Record<string, string>>): {
  formikErrors?: { [field: string]: string };
  generalErrors: Array<{ code: string; detail: string; status: number }>;
} {
  let outputFormikErrors: { [field: string]: string } | undefined = undefined;
  const outputGeneralErrors: Array<{ code: string; detail: string; status: number }> = [];

  if (!errorMessages) return { formikErrors: undefined, generalErrors: [] };
  errorMessages.forEach((error) => {
    if (!isGlueErrorDetail(error)) {
      error = {
        detail: `${error.description || error.message}`,
        status: '400',
      };
    }
    const [key, message] = error.detail.split(' => ');
    const isFormikError = error.detail.includes(' =>');

    if (message) {
      if (!outputFormikErrors) {
        outputFormikErrors = {};
      }
      if (outputFormikErrors[key]) {
        outputFormikErrors[key] = `${outputFormikErrors[key]} ${message}`;
      } else {
        outputFormikErrors[key] = message;
      }
    }
    if (!isFormikError) {
      outputGeneralErrors.push({ code: error.code, status: +error.status, detail: error.detail });
    }
  });

  return { formikErrors: outputFormikErrors, generalErrors: outputGeneralErrors };
}

export function getErrorCodeFromAnyErrorLike(error: GlueErrorResponse | Error | unknown | string): string | undefined {
  let code = undefined;
  if (
    ((typeof error === 'object' && error && 'code' in error) || isGlueErrorDetail(error)) &&
    error.code !== undefined
  ) {
    code = String(error.code);
  }

  if (isGlueErrorResponse(error) && 'code' in error.errors[0]) {
    code = error.errors[0].code;
  }

  return code ? String(JSON.parse(JSON.stringify(code))) : undefined;
}

export function getErrorMessageFromAnyErrorLike(
  error: GlueErrorResponse | Error | unknown | string | ReactNode,
): string | ReactNode {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string' || isValidElement(error)) {
    return error;
  }

  if (typeof error === 'object' && error && 'description' in error) {
    return error.description as string;
  }

  if (typeof error === 'object' && error && 'errorType' in error) {
    return error.errorType as string;
  }

  if (isGlueErrorDetail(error)) {
    return error.detail;
  }

  if (isGlueErrorResponse(error)) {
    return error.errors[0].detail;
  }

  return 'Unknown error';
}

export function isAuthenticationErrorMessage(message: string | ReactNode) {
  if (typeof message !== 'string') {
    return false;
  }

  return (
    message.toLowerCase().indexOf(UNAUTHORIZED) > -1 ||
    message.toLowerCase().indexOf(AUTH0_INVALID_SESSION_MESSAGE) > -1 ||
    message.toLowerCase().indexOf(AUTH0_MISSING_ACCESS_TOKEN_MESSAGE) > -1 ||
    message.toLowerCase().indexOf(AUTH0_INACTIVE_SESSION_MESSAGE) > -1
  );
}
export function isCartNeedsReviewErrorMessage(message: string | ReactNode) {
  if (typeof message !== 'string') {
    return false;
  }
  return message.toLowerCase().indexOf(CART_NEEDS_REVIEW) > -1;
}

export function isCartItemsPricesUpdatedTryAgainErrorCode(code: string | undefined) {
  return code === CART_PRICES_UPDATED_TRY_AGAIN_ERROR_CODE;
}

export function isCartItemsPricesCouldNotBeValidatedTryAgainErrorCode(code: string | undefined) {
  return code === CART_PRICES_NOT_VALIDATED_TRY_AGAIN_ERROR_CODE;
}

export function isStripePaymentErrorCode(code: string | undefined) {
  return code && [RESPONSE_CODE_STRIPE_PAYMENT_ERROR, RESPONSE_CODE_STRIPE_PAYMENT_AUTHENTICATION_ERROR].includes(code);
}

export function isCartContainsNonBuyableItemsErrorMessage(message: string | ReactNode) {
  if (typeof message !== 'string') {
    return false;
  }
  return message.toLowerCase().indexOf(CART_CONTAINS_NON_BUYABLE_ITEMS) > -1;
}

export function isCustomerNotFoundErrorMessage(message: string) {
  return message.toLowerCase().indexOf(CUSTOMER_NOT_FOUND) > -1;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function errorStatusCodeEquals(error: any, code: HTTP_STATUS) {
  return error.status === code;
}
